// The two tutorial tracks from #2991. Copy is Brian's, ported verbatim from
// the design prototype (design-prototypes docs/handoff/2991-tutorial.md) —
// review copy changes there, not here. Step ids also live in
// app/models/demo_tour.rb (the completion whitelist); keep the lists in sync.
//
// Unlike a linear product tour, each step is a spoke: the checklist launches
// it, the step plays a sequence of `beats` inside its own screen, and
// finishing returns to the checklist with that item ticked. Steps can be
// taken in any order.
//
// Beat fields:
//   anchor  - data-tutorial name, or a [from, to] pair bracketing a run of
//             table columns (resolved against the existing th.col-* classes)
//   dim     - white wash over the rest of the screen (default true); clicks
//             are blocked either way
//   scroll  - leave the screen's own scrolling working (default false)
//   square  - trim the ring to a square on the anchor's smaller side
//   beside  - 'left' or 'right' puts the card beside the anchor, top edges
//             aligned ('right' is forced and clamps on narrow viewports)
//   pin     - 'above' or 'below' forces the card against that edge of the
//             anchor, skipping the room heuristic — for modal beats the
//             card must not cover the modal
//   place   - card position when there is no anchor
//   route   - symbolic key into the server route map when the beat plays on a
//             different screen from the step's own; advancing navigates
//   open    - UI state the beat needs: { modal: sel } or { dropdown: sel }
//             (sel targets the toggle for dropdowns, the modal element for
//             modals); closed again when the beat is left
//   requiresPro - beat is dropped when the org has no pro features

export const STEPS = [
  {
    id: "gd-send-referral",
    persona: "gd",
    label: "Send a referral",
    blurb:
      "Search Sindi or send to any office email. Attach imaging up to 500MB, and it arrives encrypted.",
    route: "newReferral",
    minutes: 1,
    beats: [
      {
        title: "Choose how to send",
        body: [
          "Search for the office within Sindi's database, or send to any office email — no account needed on their end to receive it. It'll arrive encrypted and secure.",
          "If you send to an email, they'll get a notification and just need to create a free account and password to view the referral.",
        ],
        cta: "Next",
        anchor: "referral-recipient",
        dim: false,
      },
      {
        title: "Attach files",
        body: [
          "Attach multiple files up to 500MB each — enough for cone beam (CBCT) scans, STL files, DICOMs, JPEGs, and more. Stop burning CDs!",
          "Free accounts cap at 50MB per file; Pro unlocks the full 500MB.",
        ],
        cta: "Next",
        anchor: "referral-cbct",
        dim: false,
      },
      {
        // Nothing is actually sent here — the beat shows where the button is,
        // and the next one picks the story up on a referral that has gone out.
        title: "Send it",
        body: [
          "When you're ready, hit Send Referral, or Save as Draft to finish later.",
        ],
        cta: "Next",
        anchor: "referral-submit",
        dim: false,
      },
      {
        title: "Send a copy to the patient",
        body: [
          "Send a copy straight to your patient by text or email. It saves you a step and lets them schedule their own appointment if they'd rather handle it themselves.",
        ],
        cta: "Finish",
        anchor: "send-patient-copy",
        dim: false,
        route: "outboundDetail",
        open: { dropdown: "#referralMoreActions" },
      },
    ],
  },
  {
    id: "gd-referral-status",
    persona: "gd",
    label: "Check referral status",
    blurb: "Every referral you've sent, searchable, with scheduling updates.",
    route: "outboundDashboard",
    minutes: 1,
    beats: [
      {
        title: "Find your outbound referrals",
        body: [
          "Sent a referral? Here's where to find it again. You'll get a centralized, searchable database of every referral you've ever sent — so it's never buried or hard to track down.",
        ],
        cta: "Next",
        anchor: "referrals-tab-outbound",
      },
      {
        title: "Get the updates you need",
        body: [
          "Search any referral to see exactly where it stands. If a specialist schedules a patient, you'll know about it. No more phone tag — you'll always know where your patient is in the process.",
        ],
        cta: "Next",
        anchor: ["phase", "snooze"],
      },
      {
        title: "What is the timeline?",
        body: [
          "Every referral has its own timeline — a complete digital record of everything that's happened, from the moment it was sent to today.",
          "It's also where you and the other office can talk directly about the case, all in one secure, central place. No more scattered email threads, texts, or phone tag.",
        ],
        cta: "Done",
        anchor: "timeline-panel",
        route: "outboundDetail",
      },
    ],
  },
  buildMessaging("gd-messaging", "gd"),
  {
    id: "sp-inbound-dashboard",
    persona: "specialist",
    label: "Manage your inbound referrals",
    blurb:
      "Every referral you've received, organized. Tools at your fingertips so referrals don't slip through the cracks.",
    route: "inboundDashboard",
    minutes: 1,
    beats: [
      {
        title: "Your referral database",
        body: [
          "Every referral you receive lands in one searchable database. New referrals show up right in your New Referrals section, so nothing gets missed.",
        ],
        cta: "Next",
        anchor: "new-referrals-table",
      },
      {
        title: "Accept a referral",
        body: [
          "Open a new referral to review the details. Accept it to add it to your inbound dashboard.",
        ],
        cta: "Next",
        anchor: "referral-accept",
        route: "inboundPending",
      },
      {
        title: "Phase & status",
        body: [
          "Phase of care tracks where a case stands; Consultation, Treatment, Follow-up. Status tracks conversion; Scheduled or Unscheduled.",
          "Together, they show you exactly where every patient is, at a glance.",
        ],
        cta: "Next",
        anchor: ["phase", "status"],
      },
      {
        title: "Snooze",
        body: [
          "Not ready to act yet? Snooze a referral to set a reminder and circle back later. Nothing slips through the cracks.",
        ],
        cta: "Next",
        anchor: "snooze-cell",
        square: true,
        // The snooze icons run down a column, so the space under one of them
        // is the rest of them.
        beside: "left",
      },
      {
        title: "Update a referral",
        body: [
          "Click the phase and status in the upper right to update where a case stands.",
        ],
        cta: "Next",
        anchor: "phase-status-control",
        route: "inboundDetail",
      },
      {
        title: "Change phase & status",
        body: [
          "Advance the phase, mark it as scheduled, and more. The referring office is notified immediately, so they always know where their patient stands.",
        ],
        cta: "Next",
        anchor: "status-modal",
        route: "inboundDetail",
        open: { modal: "#modal_schedule_appointment" },
        // The card must not cover the modal it is describing.
        pin: "above",
      },
      {
        title: "The timeline",
        body: [
          "The timeline is a complete digital record of everything that's happened with a case. Export it directly into the patient chart, or use it to discuss the case with the referring doctor when needed.",
        ],
        cta: "Finish",
        anchor: "timeline-panel",
        route: "inboundDetail",
      },
    ],
  },
  {
    id: "sp-embed-form",
    persona: "specialist",
    label: "Put your referral form on your website",
    blurb:
      "A fully customizable form. Embed it on your website. It arrives straight into your Sindi database.",
    route: "formConfigs",
    minutes: 1,
    beats: [
      {
        // The beat is the form itself, so it keeps its contrast and its
        // scrolling: the form runs past the fold, and a reader who cannot
        // reach the bottom of it has not seen what the beat is claiming.
        title: "Fully customizable",
        body: [
          "Your referral form is fully customizable. Make edits, and add it to your website. Exclusively for Pro members.",
        ],
        cta: "Next",
        place: "bottom-left",
        dim: false,
        scroll: true,
      },
      {
        title: "Ask exactly what you need",
        body: [
          "Add custom questions to capture exactly the information you need.",
        ],
        cta: "Next",
        // Ring the field-options menu itself (form-add-fields is on the open
        // dropdown menu), opened via its toggle.
        anchor: "form-add-fields",
        open: { dropdown: '[data-tutorial="form-add-fields-toggle"]' },
      },
      {
        title: "Add it to your site",
        body: [
          "Referral forms can be added to your practice website in just a few clicks. There's no excuse for a paper form or a PDF sitting on your website. Every submission flows straight into your Sindi database.",
        ],
        cta: "Next",
        // Shown to free orgs too: any org can embed its form (customizations
        // just don't go live until Pro), so the embed steps are not pro-gated.
        anchor: "form-embed-bar",
      },
      {
        title: "One line of code",
        body: [
          "Copy this single line of code into your website's HTML, and your form appears — live, embedded, and connected to Sindi.",
        ],
        cta: "Finish",
        anchor: "form-embed-modal",
        open: { modal: "#embedInstructionsModal" },
        // Beside the instructions, top edges aligned — the modal is too tall
        // for the card above or below it.
        beside: "right",
      },
    ],
  },
  // Same position as on the general dentist track: third row, right before
  // Build your network.
  buildMessaging("sp-messaging", "specialist"),
];

// Message another office appears on both tracks. One module, two entries:
// the screen, beats and copy are identical for either persona, and the
// checklist is filtered by persona, so it exists once per track.
// (Function declaration, so it hoists above the STEPS literal that calls it.)
function buildMessaging(id, persona) {
  return {
    id,
    persona,
    label: "Message another office",
    blurb:
      "Encrypted messaging with any office. Modern communication for modern practices.",
    route: "conversations",
    minutes: 1,
    beats: [
      {
        title: "Your messaging dashboard",
        body: [
          "All your conversations live in one place. No more digging through email threads or missed calls — just open Sindi and everything's there, ready to pick up instantly.",
        ],
        cta: "Next",
        anchor: "messages-rail",
      },
      {
        title: "Start a message",
        body: [
          "Just like sending a referral, you can message any organization within Sindi, or send to an office email. If they're outside Sindi, they'll get an encrypted link and just need to create a free account to reply.",
        ],
        cta: "Next",
        anchor: "messages-new",
        // The modal's body is fetched on demand — open it through its remote
        // trigger (as the app does) so the form loads, not the bare shell.
        open: {
          modal: "#new-conversation-modal",
          trigger: "a[href*='conversations/new'][data-remote='true']",
        },
      },
      {
        title: "Do more than an email ever could",
        body: [
          "Attach images and cone beam scans. Use @ to mention specific people, or add entire organizations to a conversation — your team, a referring office, whoever needs to be in the loop. Use # to link directly to a referral.",
          "This is everything a phone call, text, or fax can't do.",
        ],
        cta: "Finish",
        anchor: "messages-composer",
        // Opens a thread (a real conversation, else a seeded sample) so the
        // composer this beat describes is actually on screen.
        route: "conversationsDetail",
        // The beat is about what a message can carry, so the thread it is
        // carried in stays readable.
        dim: false,
      },
    ],
  };
}

// Build your network closes both tracks. One step, two entries: the screen
// and the beats are identical for either persona, and the checklist is
// filtered by persona, so it exists once per track. The blurb is the one
// thing that differs.
function buildNetwork(id, persona, blurb) {
  return {
    id,
    persona,
    label: "Build your network",
    blurb,
    route: "home",
    minutes: 1,
    beats: [
      {
        title: "Your curated network",
        body: [
          "These are the people you work with most. Favorite them for quick access, so sending a referral is always just one click away.",
        ],
        cta: "Next",
        anchor: "home-network",
      },
      {
        // Advancing scrolls the panel to the suggestions, which is the beat:
        // they are under the list you already have.
        title: "Suggestions near you",
        body: [
          "Keep scrolling to see other offices near you. Reach out and start building your network — the more connections, the smoother your referrals.",
        ],
        cta: "Next",
        anchor: "home-network-nearby",
      },
      {
        title: "Can't find them? Invite them",
        body: [
          "Don't see who you're looking for? Invite anyone by office email and start referring right away.",
        ],
        cta: "Finish",
        anchor: "home-network-invite",
      },
    ],
  };
}

STEPS.push(
  buildNetwork(
    "gd-build-network",
    "gd",
    "Find the doctors you work with the most and put them on speed dial."
  )
);
STEPS.push(
  buildNetwork(
    "sp-build-network",
    "specialist",
    "Invite the dentists you work with the most.\nMake their life easier."
  )
);

export function stepsFor(persona) {
  return STEPS.filter((s) => s.persona === persona);
}

export function getStep(id) {
  const step = STEPS.find((s) => s.id === id);
  if (!step) throw new Error(`Unknown tutorial step: ${id}`);
  return step;
}

// The lines a blurb is drawn as. A newline in the copy is a hard break;
// within a line the last two words are tied with a non-breaking space so a
// wrap never leaves one word alone at the bottom of a row.
export function blurbLines(blurb) {
  return blurb.split("\n").map((line) => line.replace(/ (\S+)$/, " $1"));
}
